import { Component, OnInit, OnChanges, OnDestroy, Input, SimpleChanges } from '@angular/core';
import { PanelBarItemModel, PanelBarExpandMode } from '@progress/kendo-angular-layout';
import { SessionService } from '../../../../services/session/session.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ags-habilitation-electrique',
  templateUrl: './habilitation-electrique.component.html',
  styleUrls: ['./habilitation-electrique.component.scss']
})
export class HabilitationElectriqueComponent implements OnInit, OnChanges, OnDestroy {
  public expandMode: number = PanelBarExpandMode.Single;
  public kendoPanelBarExpandMode: typeof PanelBarExpandMode = PanelBarExpandMode;
  public listIndiceCategories: PanelBarItemModel[] = [];

  // Inputs
  @Input() infoApprenant: any;
  @Input() infoHabilitation: any;

  // Données internes
  public habilitation: any;
  public listItems: string[] = ['FAVORABLE', 'DEFAVORABLE'];
  public listDomaineTension: Array<{ id: any; libelle: string }> = [];
  public listOuvrageInstallation: Array<{ id: any; libelle: string }> = [];
  public listLineHabilitation: any[] = [];
  public listCurrentLineHab: any[] = [];

  public originCurrentLine: any;
  public expandedPanelItem: any;

  // Subject pour l'autosave avec debounce
  private autoSaveSubject = new Subject<void>();
  private unsubscribe$ = new Subject<void>();

  constructor(private sessionService: SessionService) {}

  ngOnInit() {
    this.initializeComponent();

    // Lancer la sauvegarde 1 seconde après le dernier changement
    this.autoSaveSubject.pipe(
      debounceTime(1000),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => {
      this.save();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['infoHabilitation'] && !changes['infoHabilitation'].firstChange) {
      this.initializeComponent();
    }
    if (changes['infoApprenant'] && !changes['infoApprenant'].firstChange) {
      this.getInfoHabilitation();
    }
  }

  initializeComponent(): void {
    if (!this.infoHabilitation) {
      console.warn('infoHabilitation est manquant');
      return;
    }
    this.getInfoHabilitation();

    if (this.infoHabilitation.categories && this.infoHabilitation.categories.default) {
      this.listIndiceCategories = Object.keys(this.infoHabilitation.categories.default).map(key => ({
        title: `${this.infoHabilitation.categories.default[key]}`,
        content: `${key}`
      } as PanelBarItemModel));
    }

    this.listDomaineTension = this.infoHabilitation.domainesTension || [];
    this.listOuvrageInstallation = this.infoHabilitation.ouvrages || [];
  }

  getInfoHabilitation(): void {
    if (this.infoApprenant && this.infoApprenant.habilitation) {
      // Création d'une copie pour éviter la mutation de l'objet d'origine
      this.habilitation = JSON.parse(JSON.stringify(this.infoApprenant.habilitation));
      this.habilitation.nbHeures = Number(this.habilitation.nbHeures) || 0;
      this.habilitation.dateDelivrance = this.habilitation.dateDelivrance
        ? new Date(this.habilitation.dateDelivrance)
        : null;
      this.habilitation.dateFinValidite = this.habilitation.dateFinValidite
        ? new Date(this.habilitation.dateFinValidite)
        : null;
    } else {
      console.warn('infoApprenant.habilitation est manquant');
    }
  }

  compareLineHab(currentObject: any, originObject: any, panelItem: any): void {
    Object.keys(currentObject).forEach(key => {
      const currentLine = currentObject[key];
      const originLine = originObject[currentLine.id];
      if (originLine) {
        Object.keys(currentLine).forEach(prop => {
          originLine[prop] = currentLine[prop];
          if (prop === 'domaineTensionItem' && currentLine[prop]) {
            originLine['domaineTension'] = currentLine[prop].id;
          }
          if (prop === 'ouvrageItem' && currentLine[prop]) {
            originLine['ouvrage'] = currentLine[prop].id;
          }
        });
      }
    });
    if (this.habilitation && this.habilitation.indices && panelItem && panelItem.content) {
      this.habilitation.indices[panelItem.content] = originObject;
    }
  }

  getLineHabilitation($event: any): void {
    // Lors de l'expansion d'un panel, on travaille sur l'objet local déjà à jour
    if (this.originCurrentLine && this.listCurrentLineHab && this.expandedPanelItem) {
      this.compareLineHab(this.listCurrentLineHab, this.originCurrentLine, this.expandedPanelItem);
    }

    this.expandedPanelItem = $event.items.find((item: any) => item.expanded === true);
    this.listCurrentLineHab = [];
    if (this.habilitation && this.habilitation.indices && this.expandedPanelItem) {
      const indicesForPanel = this.habilitation.indices[this.expandedPanelItem.content];
      if (!indicesForPanel) {
        console.warn('Indices introuvables pour le panel:', this.expandedPanelItem.content);
        return;
      }
      // Conserver une copie pour comparaison
      this.originCurrentLine = JSON.parse(JSON.stringify(indicesForPanel));

      Object.keys(indicesForPanel).forEach(key => {
        const line = indicesForPanel[key];
        const idDomainTension = line.domaineTension;
        const idOuvrage = line.ouvrage;

        line.domaineTensionItem = this.listDomaineTension.find(
          res => Number(res.id) === Number(idDomainTension)
        );
        line.ouvrageItem = this.listOuvrageInstallation.find(
          res => Number(res.id) === Number(idOuvrage)
        );
        const indiceHab = this.infoHabilitation.indices.find((ele: any) => Number(ele.id) === Number(key));
        if (indiceHab) {
          this.listCurrentLineHab.push({ id: key, ...line });
        }
      });
    }
  }

  getIndiceLibelle(indice: any): string {
    const indiceHab = this.infoHabilitation.indices.find((el: any) => Number(el.id) === Number(indice.id));
    return indiceHab ? indiceHab.libelle : '';
  }

  // Appelée à chaque modification sur un champ
  onChange(): void {
    this.autoSaveSubject.next();
  }

  save(): void {
    if (this.originCurrentLine && this.listCurrentLineHab && this.expandedPanelItem) {
      this.compareLineHab(this.listCurrentLineHab, this.originCurrentLine, this.expandedPanelItem);
    }
    this.sessionService.updateSessionApprenantHabilitation(this.infoApprenant.id, this.habilitation)
      .subscribe({
         next: (response) => {
           console.log('Sauvegarde réussie', response);
           // Mise à jour de l'input pour conserver les données actualisées
           this.infoApprenant.habilitation = JSON.parse(JSON.stringify(this.habilitation));
         },
         error: (error) => {
           console.error('Erreur lors de la sauvegarde', error);
         }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
